import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle } from 'lucide-react';

export const CICLOS = {
  maternal: 'Maternal',
  jardin: 'Jardín',
  primaria_1_3: 'Primaria 1°-3° año',
  primaria_4_6: 'Primaria 4°-6° año',
  secundaria: 'Secundaria',
};

const hijoVacio = () => ({ nombre_apellido: '', dni: '', edad_2027: '', ciclo: '' });

export const respuestaVacia = () => ({ tiene_hijos: null, cantidad_hijos: '', hijos: [], posee_cud: null, interes_programa: null });

const SiNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {[true, false].map((v) => (
      <button
        type="button"
        key={String(v)}
        onClick={() => onChange(v)}
        data-testid={`${testid}-${v ? 'si' : 'no'}`}
        className={`px-6 py-2 rounded-full border text-sm font-medium transition-colors ${
          value === v ? 'bg-primary text-white border-primary' : 'bg-white hover:bg-gray-50 border-gray-300'
        }`}
      >
        {v ? 'Sí' : 'No'}
      </button>
    ))}
  </div>
);

const HijoCampos = ({ hijo, i, onChange }) => {
  const set = (k, v) => onChange({ ...hijo, [k]: v });
  return (
    <div className="rounded-lg border bg-gray-50 p-4 space-y-3" data-testid={`hijo-bloque-${i}`}>
      <p className="text-sm font-semibold text-gray-700">Hijo/a {i + 1}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label>Nombre y Apellido</Label>
          <Input value={hijo.nombre_apellido} onChange={(e) => set('nombre_apellido', e.target.value)} data-testid={`hijo-${i}-nombre`} />
        </div>
        <div>
          <Label>DNI</Label>
          <Input inputMode="numeric" value={hijo.dni} onChange={(e) => set('dni', e.target.value.replace(/\D/g, ''))} data-testid={`hijo-${i}-dni`} />
        </div>
        <div>
          <Label>Edad en 2027</Label>
          <Input type="number" min={0} max={17} value={hijo.edad_2027} onChange={(e) => set('edad_2027', e.target.value)} data-testid={`hijo-${i}-edad`} />
        </div>
        <div className="sm:col-span-2">
          <Label>¿En qué ciclo estará en 2027?</Label>
          <select
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={hijo.ciclo}
            onChange={(e) => set('ciclo', e.target.value)}
            data-testid={`hijo-${i}-ciclo`}
          >
            <option value="">Elegí una opción…</option>
            {Object.entries(CICLOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
};

export const validarRespuesta = (r) => {
  if (r.tiene_hijos === null) return 'Indicá si tenés hijos en edad escolar.';
  if (r.tiene_hijos) {
    const n = Number(r.cantidad_hijos);
    if (!n || n < 1) return 'Indicá cuántos hijos.';
    for (const [i, h] of r.hijos.entries()) {
      if (h.nombre_apellido.trim().length < 3) return `Completá el nombre del hijo/a ${i + 1}.`;
      if (!/^\d{7,9}$/.test(h.dni)) return `El DNI del hijo/a ${i + 1} debe tener entre 7 y 9 números.`;
      const e = Number(h.edad_2027);
      if (h.edad_2027 === '' || !(e >= 0 && e <= 17)) return `La edad en 2027 del hijo/a ${i + 1} debe estar entre 0 y 17.`;
      if (!h.ciclo) return `Elegí el ciclo del hijo/a ${i + 1}.`;
    }
    if (r.posee_cud === null || r.posee_cud === undefined) return 'Indicá si alguno de tus hijos posee CUD.';
  }
  if (r.interes_programa === null) return 'Indicá si te interesa participar del programa.';
  return null;
};

export const payloadRespuesta = (r) => ({
  tiene_hijos: r.tiene_hijos,
  cantidad_hijos: r.tiene_hijos ? Number(r.cantidad_hijos) : 0,
  hijos: r.tiene_hijos ? r.hijos.map((h) => ({ ...h, nombre_apellido: h.nombre_apellido.trim(), edad_2027: Number(h.edad_2027) })) : [],
  posee_cud: r.tiene_hijos ? r.posee_cud : null,
  interes_programa: r.interes_programa,
});

export const FormularioFamiliar = ({ value, onChange }) => {
  const setCantidad = (txt) => {
    const n = Math.max(0, Math.min(15, parseInt(txt, 10) || 0));
    const hijos = Array.from({ length: n }, (_, i) => value.hijos[i] || hijoVacio());
    onChange({ ...value, cantidad_hijos: txt === '' ? '' : String(n), hijos });
  };
  const setHijo = (i, h) => onChange({ ...value, hijos: value.hijos.map((x, j) => (j === i ? h : x)) });

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <p className="font-medium text-gray-900">¿Tenés hijos en edad escolar (hasta 17 años) en 2027?</p>
        <SiNo value={value.tiene_hijos} onChange={(v) => onChange({ ...value, tiene_hijos: v })} testid="tiene-hijos" />
      </section>

      {value.tiene_hijos && (
        <section className="space-y-4">
          <div className="max-w-[160px]">
            <Label>¿Cuántos?</Label>
            <Input type="number" min={1} max={15} value={value.cantidad_hijos} onChange={(e) => setCantidad(e.target.value)} data-testid="cantidad-hijos-input" />
          </div>
          {value.hijos.map((h, i) => <HijoCampos key={i} hijo={h} i={i} onChange={(nh) => setHijo(i, nh)} />)}
          <div className="space-y-3 pt-2">
            <p className="font-medium text-gray-900">¿Alguno de tus hijos posee CUD (Certificado Único de Discapacidad)?</p>
            <SiNo value={value.posee_cud} onChange={(v) => onChange({ ...value, posee_cud: v })} testid="posee-cud" />
          </div>
        </section>
      )}

      <section className="space-y-3">
        <p className="font-medium text-gray-900">
          ¿Te interesaría participar de un programa institucional que acompañe el inicio de clases 2027 con elementos escolares?
        </p>
        <SiNo value={value.interes_programa} onChange={(v) => onChange({ ...value, interes_programa: v })} testid="interes-programa" />
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="aviso-documentacion">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>En caso de indicar afirmativamente se solicitará eventualmente documentación que acredite el vínculo.</span>
        </div>
      </section>
    </div>
  );
};

export const RespuestaResumen = ({ respuesta }) => (
  <div className="space-y-4 text-sm" data-testid="respuesta-resumen">
    <p><span className="text-gray-500">Hijos en edad escolar en 2027:</span> <b>{respuesta.tiene_hijos ? `Sí (${respuesta.cantidad_hijos})` : 'No'}</b></p>
    {respuesta.hijos?.map((h, i) => (
      <div key={i} className="rounded-lg border bg-gray-50 p-3" data-testid={`resumen-hijo-${i}`}>
        <p className="font-medium">{h.nombre_apellido}</p>
        <p className="text-gray-600">DNI {h.dni} · {h.edad_2027} años en 2027 · {CICLOS[h.ciclo] || h.ciclo}</p>
      </div>
    ))}
    {respuesta.tiene_hijos && (
      <p data-testid="resumen-cud"><span className="text-gray-500">¿Algún hijo con CUD?:</span> <b>{respuesta.posee_cud === true ? 'Sí' : respuesta.posee_cud === false ? 'No' : 'Sin dato'}</b></p>
    )}
    <p><span className="text-gray-500">Interés en el programa escolar 2027:</span> <b>{respuesta.interes_programa ? 'Sí' : 'No'}</b></p>
  </div>
);

export const respuestaAEdicion = (r) => ({
  tiene_hijos: r.tiene_hijos,
  cantidad_hijos: r.tiene_hijos ? String(r.cantidad_hijos) : '',
  hijos: (r.hijos || []).map((h) => ({ ...h, edad_2027: String(h.edad_2027) })),
  posee_cud: r.posee_cud ?? null,
  interes_programa: r.interes_programa,
});
